/**
 * Reviews — card-centric worklists for distinct review jobs.
 *
 * Identity review groups active and completed Card_Records by Storage_Batch through
 * the compact identity-status index. Each batch links to the identification workbench;
 * authoritative storage status supplies Not started, In progress, and Completed state.
 * Historical dismiss state remains readable by the backend, but the Reviews worklist
 * no longer exposes dismiss, restore, delete, or bulk-selection controls.
 *
 * The queue is grouped by physical **Storage_Batch** first, then Capture_Run for
 * unplaced legacy cards. The API aggregates the complete filtered queue into
 * lightweight group summaries; full card rows load only when a group expands,
 * so a Catalog page boundary can never produce partial batch counts.
 *
 * Image approval is completed from the same card and batch workbench surfaces
 * as identity review. The approval state remains an independent backend
 * safety gate for kitting, and marketplace readiness.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { CardRecord, ImageQualityStatus, StorageBatchWorkflowStatus } from '../shared/types';
import { shortDateTime } from '../shared/datetime';
import { formatIdentity } from '../shared/PredictionView';
import {
  useVersionedImageReview,
  VersionedImageReviewStatus,
} from '../catalog/useVersionedImageReview';
import {
  type CatalogDetailResponse,
  type IdentityReviewGroupSummary,
  type IdentityReviewPreviewCard,
  useCatalogApi,
} from '../catalog/catalog-api';
import {
  positionVerticalHoverPreview,
  type HoverPreviewPosition,
} from '../storage/batch-hover-preview';
import {
  type EntityLocationResolution,
  type StorageEntityDetailResponse,
  useStorageApi,
} from '../storage/storage-api';
import {
  buildStorageGroups,
  CollapsibleStorageGroups,
  StorageGroupingControl,
  storageLocationBreadcrumb,
  type StorageGroupMode,
  type StorageHierarchySegment,
} from '../storage/StorageHierarchy';
import {
  browserStorageHierarchyStateStore,
  readStorageGroupMode,
  readStorageHierarchyExpansion,
  writeStorageGroupMode,
  writeStorageHierarchyExpansion,
} from '../storage/storage-hierarchy-state';
import { useAuth } from '../auth/AuthContext';
import {
  DEFAULT_IDENTITY_REVIEW_SORT_ORDER,
  IDENTITY_REVIEW_RESULT_LIMITS,
  browserResultLimitStorage,
  reviewListReturnContext,
  reviewListReturnContextFrom,
  readIdentityReviewResultLimitPreference,
  writeIdentityReviewResultLimitPreference,
  type IdentityReviewResultLimit,
  type IdentityReviewSortOrder,
  type ReviewListReturnContext,
} from './review-list-return-state';
import { parseReviewStorageScope, reviewStorageBatchIds } from './review-storage-scope';

export {
  DEFAULT_IDENTITY_REVIEW_RESULT_LIMIT,
  IDENTITY_REVIEW_RESULT_LIMITS,
  type IdentityReviewResultLimit,
} from './review-list-return-state';

type ImageApprovalView = ImageQualityStatus | 'ALL';
type ReviewsTab = 'identity' | 'image-issues';
export const IDENTITY_REVIEW_PROGRESS_BATCH_SIZE = 5;

export function reviewsTabFromSection(section: string | null): ReviewsTab {
  return section?.trim().toLowerCase() === 'image issues' ? 'image-issues' : 'identity';
}

export function visibleIdentityReviewGroups<T>(groups: readonly T[], visibleCount: number): T[] {
  return groups.slice(0, visibleCount);
}

export function sortIdentityReviewGroups(
  groups: readonly IdentityReviewGroupSummary[],
  sortOrder: IdentityReviewSortOrder,
): IdentityReviewGroupSummary[] {
  return [...groups].sort((left, right) => {
    const leftValid = Number.isFinite(left.latest);
    const rightValid = Number.isFinite(right.latest);
    if (!leftValid || !rightValid) {
      if (leftValid) return -1;
      if (rightValid) return 1;
      return left.key.localeCompare(right.key);
    }
    const byDate = sortOrder === 'newest' ? right.latest - left.latest : left.latest - right.latest;
    return byDate === 0 ? left.key.localeCompare(right.key) : byDate;
  });
}

export function nextIdentityReviewVisibleCount(
  current: number,
  resultLimit: IdentityReviewResultLimit,
): number {
  return current + resultLimit;
}

function csvCell(value: unknown): string {
  const text = value === undefined || value === null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

export function manualIdentityOverrideCsv(cards: readonly CardRecord[]): string {
  const headers = [
    'Card ID',
    'Short ID',
    'Batch ID',
    'Category',
    'Year',
    'Brand',
    'Set',
    'Subset',
    'Parallel',
    'Card Number',
    'Name',
    'Team',
    'Override Status',
    'Reviewer',
    'Reason',
    'Reviewed At',
  ];
  const rows = cards.map((card) => {
    const draft = card.identityReviewDraft;
    const audit =
      draft?.manualIdentityOverrideReason === undefined
        ? card.manualIdentityOverride
        : {
            status: 'DRAFT',
            reason: draft.manualIdentityOverrideReason,
            reviewedBy: draft.updatedBy,
            reviewedAt: draft.updatedAt,
          };
    const identity =
      audit?.status === 'DRAFT'
        ? (card.identityReviewDraft?.identity ?? card.identity)
        : (card.identity ?? card.identityReviewDraft?.identity);
    return [
      card.cardId,
      card.shortCode,
      card.placementBatchId,
      identity?.category,
      identity?.year,
      identity?.brand,
      identity?.set,
      identity?.subset,
      identity?.variation,
      identity?.number,
      identity?.name,
      identity?.team,
      audit?.status,
      audit?.reviewedBy,
      audit?.reason,
      audit?.reviewedAt,
    ];
  });
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');
}

export async function loadIdentityReviewBatchDetails(
  batchIds: readonly string[],
  loadDetail: (batchId: string) => Promise<StorageEntityDetailResponse | null>,
  onChunk: (details: ReadonlyMap<string, StorageEntityDetailResponse | null>) => boolean | void,
): Promise<void> {
  for (let index = 0; index < batchIds.length; index += IDENTITY_REVIEW_PROGRESS_BATCH_SIZE) {
    const chunk = batchIds.slice(index, index + IDENTITY_REVIEW_PROGRESS_BATCH_SIZE);
    const settled = await Promise.allSettled(
      chunk.map((batchId) => Promise.resolve().then(() => loadDetail(batchId))),
    );
    const details = new Map<string, StorageEntityDetailResponse | null>();
    settled.forEach((result, resultIndex) => {
      const batchId = chunk[resultIndex];
      if (batchId !== undefined) {
        details.set(batchId, result.status === 'fulfilled' ? result.value : null);
      }
    });
    if (onChunk(details) === false) return;
  }
}

export interface IdentityReviewLocationBucket<T> {
  key: string;
  label: string;
  groups: T[];
}

function storageLocationLabelPart(kind: 'Shelf' | 'Box' | 'Row' | 'Batch', value: string): string {
  const trimmed = value.trim();
  return trimmed.toLowerCase().startsWith(kind.toLowerCase()) ? trimmed : `${kind} ${trimmed}`;
}

function storageEntityShortId(entityId: string): string {
  return entityId.replace(/^tcvs:[^:]+:/, '');
}

function identityReviewLocationLabel(location: EntityLocationResolution | null | undefined): {
  key: string;
  label: string;
} {
  if (location === undefined) return { key: 'loading', label: 'Checking storage location' };
  if (location === null) return { key: 'unavailable', label: 'Storage location unavailable' };

  const path = location.path;
  if (location.status === 'UNPLACED' && path === undefined) {
    return { key: 'unplaced', label: 'Not put away' };
  }

  const shelf = path?.shelf;
  const box = path?.box;
  const row = path?.row;
  const key = [
    shelf?.entityId ?? 'no-shelf',
    box?.entityId ?? 'no-box',
    row?.entityId ?? 'no-row',
    location.status,
  ].join('|');
  const label = [
    shelf === undefined
      ? 'No shelf'
      : storageLocationLabelPart('Shelf', shelf.shelfCode || storageEntityShortId(shelf.entityId)),
    box === undefined
      ? 'No box'
      : storageLocationLabelPart('Box', storageEntityShortId(box.entityId)),
    row === undefined
      ? 'No row'
      : storageLocationLabelPart('Row', row.rowCode || storageEntityShortId(row.entityId)),
    ...(location.status === 'UNPLACED' ? ['Not fully put away'] : []),
  ].join(' / ');
  return { key, label };
}

export function groupIdentityReviewBatchesByLocation<T extends { key: string; batchId?: string }>(
  groups: readonly T[],
  details: ReadonlyMap<string, StorageEntityDetailResponse | null>,
): IdentityReviewLocationBucket<T>[] {
  const buckets = new Map<string, IdentityReviewLocationBucket<T>>();
  for (const group of groups) {
    const detail = group.batchId === undefined ? null : details.get(group.batchId);
    const location = detail === undefined || detail === null ? detail : detail.location;
    const resolved =
      group.batchId === undefined
        ? { key: 'legacy-run', label: 'No storage batch' }
        : identityReviewLocationLabel(location);
    const existing = buckets.get(resolved.key);
    if (existing === undefined) {
      buckets.set(resolved.key, { ...resolved, groups: [group] });
    } else {
      existing.groups.push(group);
    }
  }
  return [...buckets.values()];
}

export function identityReviewStorageHierarchyPath<T extends { batchId?: string }>(
  group: T,
  details: ReadonlyMap<string, StorageEntityDetailResponse | null>,
): StorageHierarchySegment[] {
  if (group.batchId === undefined) {
    return [{ level: 'shelf', key: 'legacy-run', label: 'No storage batch' }];
  }
  const detail = details.get(group.batchId);
  if (detail === undefined) {
    return [{ level: 'shelf', key: 'loading', label: 'Checking storage location' }];
  }
  if (detail === null) {
    return [{ level: 'shelf', key: 'unavailable', label: 'Storage location unavailable' }];
  }
  const path = detail.location.path;
  if (detail.location.status === 'UNPLACED' && path === undefined) {
    return [{ level: 'shelf', key: 'unplaced', label: 'Not put away' }];
  }
  const shelf = path?.shelf;
  const box = path?.box;
  const row = path?.row;
  return [
    {
      level: 'shelf',
      key: shelf?.entityId ?? 'no-shelf',
      label:
        shelf === undefined
          ? 'No shelf'
          : storageLocationLabelPart(
              'Shelf',
              shelf.shelfCode || storageEntityShortId(shelf.entityId),
            ),
    },
    {
      level: 'box',
      key: box?.entityId ?? 'no-box',
      label:
        box === undefined
          ? 'No box'
          : storageLocationLabelPart('Box', storageEntityShortId(box.entityId)),
    },
    {
      level: 'row',
      key: row?.entityId ?? 'no-row',
      label:
        row === undefined
          ? 'No row'
          : storageLocationLabelPart('Row', row.rowCode || storageEntityShortId(row.entityId)),
    },
    {
      level: 'batch',
      key: group.batchId,
      label: storageLocationLabelPart('Batch', storageEntityShortId(group.batchId)),
    },
  ];
}

const IMAGE_APPROVAL_STATUSES: ImageQualityStatus[] = ['PENDING', 'ACCEPTED', 'REJECTED'];
const IMAGE_APPROVAL_VIEWS: ImageApprovalView[] = [...IMAGE_APPROVAL_STATUSES, 'ALL'];

export interface IdentityReviewStatusFilters {
  hideIdentityReviewed: boolean;
  hideImageReviewed: boolean;
  showInProgress: boolean;
  showCompleted: boolean;
}

export type IdentityReviewProgressState = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export function identityReviewProgressState(
  status: StorageBatchWorkflowStatus,
): IdentityReviewProgressState {
  if (status.reviewComplete === true) return 'COMPLETED';
  if ((status.review?.startedCount ?? 0) > 0) return 'IN_PROGRESS';
  return 'NOT_STARTED';
}

export function identityReviewStatusVisible(
  status: StorageBatchWorkflowStatus | null | undefined,
  filters: IdentityReviewStatusFilters,
): boolean {
  if (status === null || status === undefined) return true;
  if (filters.hideIdentityReviewed && status.reviewComplete === true) return false;
  if (filters.hideImageReviewed && status.preliminaryImageReviewComplete === true) return false;
  if (!filters.showInProgress && !filters.showCompleted) return true;
  const progressState = identityReviewProgressState(status);
  if (progressState === 'IN_PROGRESS') return filters.showInProgress;
  if (progressState === 'COMPLETED') return filters.showCompleted;
  return false;
}

export function imageApprovalViewLabel(view: ImageApprovalView): string {
  switch (view) {
    case 'PENDING':
      return 'Pending';
    case 'ACCEPTED':
      return 'Approved';
    case 'REJECTED':
      return 'Rejected';
    case 'ALL':
      return 'All';
  }
}

export function imageQualityStatusLabel(status?: ImageQualityStatus): string {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'ACCEPTED':
      return 'Approved';
    case 'REJECTED':
      return 'Rejected';
    default:
      return 'No image approval status';
  }
}

export function emptyImageApprovalText(view: ImageApprovalView): string {
  switch (view) {
    case 'PENDING':
      return 'No cards awaiting image approval.';
    case 'ACCEPTED':
      return 'No approved cards.';
    case 'REJECTED':
      return 'No rejected cards.';
    case 'ALL':
      return 'No Image Approval cards found.';
  }
}

/** One Capture_Run's cards in the queue. `runId === null` is the catch-all
 *  section for cards with no run (single-frame captures and pre-`runId` legacy
 *  cards). `latest` is the newest card `createdAt` (epoch ms, `-Infinity` if all
 *  unparseable) used to order run sections newest-first. */
export interface RunGroup {
  runId: string | null;
  cards: CardRecord[];
  latest: number;
}

export interface IdentityReviewGroup extends RunGroup {
  key: string;
  batchId?: string;
}

/**
 * Group queue cards by their `runId`, preserving each group's incoming card
 * order. Cards whose `runId` is absent/null collapse into a single `null`
 * group. Run groups are returned newest-capture-first by their latest card;
 * the `null` ("No run") group, when present, always sorts last regardless of
 * its timestamps so real runs lead. Pure — does not mutate the input.
 */
export function groupCardsByRun(items: readonly CardRecord[]): RunGroup[] {
  const ms = (c: CardRecord): number => {
    const t = Date.parse(c.createdAt);
    return Number.isNaN(t) ? -Infinity : t;
  };
  const byRun = new Map<string | null, RunGroup>();
  for (const card of items) {
    const key = typeof card.runId === 'string' && card.runId !== '' ? card.runId : null;
    const group = byRun.get(key);
    if (group === undefined) {
      byRun.set(key, { runId: key, cards: [card], latest: ms(card) });
    } else {
      group.cards.push(card);
      group.latest = Math.max(group.latest, ms(card));
    }
  }
  return [...byRun.values()].sort((a, b) => {
    // "No run" always last; otherwise newest run first.
    if (a.runId === null) return b.runId === null ? 0 : 1;
    if (b.runId === null) return -1;
    return b.latest - a.latest;
  });
}

/** Short, human label for a run section heading. Full run id is kept in the
 *  `title` tooltip; the heading shows a recognizable trailing slice. */
function runHeading(runId: string | null): string {
  if (runId === null) return 'No run';
  return `Run …${runId.slice(-6)}`;
}

function placementBatchIdOf(card: CardRecord): string | undefined {
  const raw = (card as CardRecord & { placementBatchId?: unknown }).placementBatchId;
  return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : undefined;
}

/**
 * Identity review is checklist-target-centric. Stored cards group by their
 * physical Storage_Batch first, so manual/no-run batches each retain an
 * independent checklist selector. Cards without a placement batch fall back
 * to Capture_Run grouping; true orphans remain isolated from valid batches.
 */
export function groupCardsForIdentityReview(items: readonly CardRecord[]): IdentityReviewGroup[] {
  const ms = (card: CardRecord): number => {
    const parsed = Date.parse(card.createdAt);
    return Number.isNaN(parsed) ? -Infinity : parsed;
  };
  const groups = new Map<string, IdentityReviewGroup>();

  for (const card of items) {
    const batchId = placementBatchIdOf(card);
    const runId = typeof card.runId === 'string' && card.runId !== '' ? card.runId : null;
    const key =
      batchId !== undefined ? `batch:${batchId}` : runId !== null ? `run:${runId}` : 'orphan';
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, {
        key,
        ...(batchId === undefined ? {} : { batchId }),
        runId,
        cards: [card],
        latest: ms(card),
      });
    } else {
      existing.cards.push(card);
      existing.latest = Math.max(existing.latest, ms(card));
    }
  }

  return [...groups.values()].sort((a, b) => {
    if (a.key === 'orphan') return b.key === 'orphan' ? 0 : 1;
    if (b.key === 'orphan') return -1;
    return b.latest - a.latest;
  });
}

export function storageBatchIdForChecklistGroup(group: RunGroup): string | undefined {
  const batchIds = group.cards.map(placementBatchIdOf);
  if (batchIds.some((batchId) => batchId === undefined)) return undefined;
  const ids = new Set(batchIds);
  return ids.size === 1 ? [...ids][0] : undefined;
}

export type ChecklistTarget = { kind: 'run'; id: string } | { kind: 'batch'; id: string };

export function checklistTargetForGroup(group: RunGroup): ChecklistTarget | null {
  const batchId = storageBatchIdForChecklistGroup(group);
  if (batchId !== undefined) return { kind: 'batch', id: batchId };
  return group.runId === null ? null : { kind: 'run', id: group.runId };
}

function identityReviewHeading(group: Pick<IdentityReviewGroup, 'batchId' | 'runId'>): string {
  if (group.batchId !== undefined) return `Storage batch ${group.batchId}`;
  if (group.runId !== null) return runHeading(group.runId);
  return 'No storage batch';
}

function ReviewCardPreviewLink({
  cardId,
  shortCode,
}: {
  cardId: string;
  shortCode?: string;
}): JSX.Element {
  const catalog = useCatalogApi();
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const [preview, setPreview] = useState<IdentityReviewPreviewCard | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [position, setPosition] = useState<HoverPreviewPosition | null>(null);

  const updatePosition = useCallback(() => {
    if (triggerRef.current === null) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition(
      positionVerticalHoverPreview(
        { left: rect.left, top: rect.top, bottom: rect.bottom },
        { width: window.innerWidth, height: window.innerHeight },
        { width: 150, height: 204 },
      ),
    );
  }, []);

  const openPreview = useCallback(() => {
    updatePosition();
    if (preview !== null || loading || failed) return;
    setLoading(true);
    void catalog.identityReviewPreviews([cardId]).then((result) => {
      setLoading(false);
      if (!result.ok || result.data.items.length === 0) {
        setFailed(true);
        return;
      }
      setPreview(result.data.items[0] ?? null);
    });
  }, [cardId, catalog, failed, loading, preview, updatePosition]);

  useEffect(() => {
    if (position === null) return;
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [position, updatePosition]);

  return (
    <>
      <Link
        ref={triggerRef}
        to={`/catalog/${cardId}`}
        className="tcvs-mono tcvs-review-card-preview-link"
        onMouseEnter={openPreview}
        onMouseLeave={() => setPosition(null)}
        onFocus={openPreview}
        onBlur={() => setPosition(null)}
      >
        {shortCode ?? cardId}
      </Link>
      {position !== null
        ? createPortal(
            <span
              className="tcvs-review-card-hover-portal"
              style={{ left: position.left, top: position.top }}
              aria-hidden="true"
            >
              {loading ? (
                <span className="tcvs-review-gallery-empty">Loading preview…</span>
              ) : failed || preview?.imageUrl === undefined ? (
                <span className="tcvs-review-gallery-empty">Preview unavailable</span>
              ) : (
                <img src={preview.imageUrl} alt="" />
              )}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}

export function BatchListPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const activeTab = reviewsTabFromSection(params.get('section'));
  const selectTab = (tab: ReviewsTab) => {
    const next = new URLSearchParams(params);
    next.set('section', tab === 'identity' ? 'Identity' : 'Image Issues');
    setParams(next);
  };

  return (
    <div>
      <h2>Reviews</h2>
      <div className="tcvs-review-view-tabs" role="tablist" aria-label="Review type">
        <button
          type="button"
          role="tab"
          id="reviews-tab-identity"
          aria-controls="reviews-panel-identity"
          aria-selected={activeTab === 'identity'}
          onClick={() => selectTab('identity')}
        >
          Identity
        </button>
        <button
          type="button"
          role="tab"
          id="reviews-tab-image-issues"
          aria-controls="reviews-panel-image-issues"
          aria-selected={activeTab === 'image-issues'}
          onClick={() => selectTab('image-issues')}
        >
          Image Issues
        </button>
      </div>
      <div
        id={`reviews-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`reviews-tab-${activeTab}`}
      >
        {activeTab === 'identity' ? <IdentityReviewSection /> : <ImageIssuesSection />}
      </div>
    </div>
  );
}

function ImageIssuesSection(): JSX.Element {
  const catalog = useCatalogApi();
  const [items, setItems] = useState<CardRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void Promise.all([
      catalog.list({
        imageQualityStatus: 'REJECTED',
        limit: 100,
      }),
      catalog.list({
        imageQualityStatus: 'PENDING',
        pendingImageReplacement: true,
        limit: 100,
      }),
    ]).then((results) => {
      if (!live) return;
      const failed = results.find((result) => !result.ok);
      if (failed !== undefined && !failed.ok) {
        setError(failed.message ?? failed.error);
        return;
      }
      const byCardId = new Map<string, CardRecord>();
      for (const result of results) {
        if (!result.ok) continue;
        for (const card of result.data.items) {
          byCardId.set(card.cardId, card);
        }
      }
      setItems(
        [...byCardId.values()].sort(
          (left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt),
        ),
      );
      setError(null);
    });
    return () => {
      live = false;
    };
  }, [catalog]);

  return (
    <section style={{ marginBottom: 28 }}>
      <h3>Image Issues</h3>
      <p className="tcvs-caption">
        Cards flagged during identification remain here until they are re-imaged or otherwise
        resolved.
      </p>
      {error !== null ? (
        <p className="tcvs-error">Error: {error}</p>
      ) : items === null ? (
        <p className="tcvs-muted">Loading image issues…</p>
      ) : items.length === 0 ? (
        <p className="tcvs-muted">No cards have unresolved image issues.</p>
      ) : (
        <ul className="tcvs-review-list">
          {items.map((card) => {
            const batchId = placementBatchIdOf(card);
            return (
              <li key={card.cardId}>
                <ReviewCardPreviewLink
                  cardId={card.cardId}
                  shortCode={card.shortCode ?? card.cardId.slice(0, 8)}
                />{' '}
                <span>{card.identity?.name ?? 'Identity not submitted'}</span>{' '}
                {batchId === undefined ? null : (
                  <span className="tcvs-muted">· Batch {batchId.replace(/^tcvs:batch:/, '')}</span>
                )}
                {card.imageQualityRejectedReason === undefined ? null : (
                  <span className="tcvs-muted"> · {card.imageQualityRejectedReason}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function IdentityReviewSection(): JSX.Element {
  const catalog = useCatalogApi();
  const storage = useStorageApi();
  const { claims } = useAuth();
  const reviewerId = claims?.sub;
  const location = useLocation();
  const navigate = useNavigate();
  const [initialReturnContext] = useState<ReviewListReturnContext | undefined>(() =>
    reviewListReturnContextFrom(location.state),
  );
  const initialSnapshot = initialReturnContext?.identityReviewListSnapshot;
  const restorePending = useRef(initialSnapshot !== undefined);
  const [groups, setGroups] = useState<IdentityReviewGroupSummary[] | null>(
    () => initialSnapshot?.groups ?? null,
  );
  const [loadedCards, setLoadedCards] = useState<Map<string, CardRecord[]>>(
    () => new Map(initialSnapshot?.loadedCards ?? []),
  );
  const [loadingGroups, setLoadingGroups] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() =>
    initialSnapshot?.expandedGroupKeys === undefined
      ? readStorageHierarchyExpansion(
          browserStorageHierarchyStateStore(),
          'reviews-batches',
          reviewerId,
        )
      : new Set(initialSnapshot.expandedGroupKeys),
  );
  const [initialResultLimit] = useState<IdentityReviewResultLimit>(
    () =>
      initialSnapshot?.resultLimit ??
      readIdentityReviewResultLimitPreference(browserResultLimitStorage(), reviewerId),
  );
  const [resultLimit, setResultLimit] = useState<IdentityReviewResultLimit>(initialResultLimit);
  const [visibleCount, setVisibleCount] = useState<number>(
    () => initialSnapshot?.visibleCount ?? initialResultLimit,
  );
  const [hideIdentityReviewed, setHideIdentityReviewed] = useState(
    () => initialSnapshot?.hideIdentityReviewed ?? true,
  );
  const [hideImageReviewed, setHideImageReviewed] = useState(
    () => initialSnapshot?.hideImageReviewed ?? false,
  );
  const [showInProgress, setShowInProgress] = useState(
    () => initialSnapshot?.showInProgress ?? false,
  );
  const [showCompleted, setShowCompleted] = useState(() => initialSnapshot?.showCompleted ?? false);
  const [previewItems, setPreviewItems] = useState<Map<string, IdentityReviewPreviewCard[]>>(
    () => new Map(initialSnapshot?.previewItems ?? []),
  );
  const [loadingPreviews, setLoadingPreviews] = useState<Set<string>>(new Set());
  const [previewErrors, setPreviewErrors] = useState<Set<string>>(new Set());
  const [batchDetails, setBatchDetails] = useState<Map<string, StorageEntityDetailResponse | null>>(
    () => new Map(initialSnapshot?.batchDetails ?? []),
  );
  const batchDetailsRef = useRef(batchDetails);
  const [searchDraft, setSearchDraft] = useState(() => initialSnapshot?.searchDraft ?? '');
  const [searchQuery, setSearchQuery] = useState(() => initialSnapshot?.searchQuery ?? '');
  const [sortOrder, setSortOrder] = useState<IdentityReviewSortOrder>(
    () => initialSnapshot?.sortOrder ?? DEFAULT_IDENTITY_REVIEW_SORT_ORDER,
  );
  const [error, setError] = useState<string | null>(null);
  const [manualOverrideReportBusy, setManualOverrideReportBusy] = useState(false);
  const [storageGroupMode, setStorageGroupMode] = useState<StorageGroupMode>(() =>
    readStorageGroupMode(browserStorageHierarchyStateStore(), 'reviews', reviewerId, 'shelf'),
  );
  const [expandedLocationGroups, setExpandedLocationGroups] = useState<Set<string>>(() =>
    readStorageHierarchyExpansion(browserStorageHierarchyStateStore(), 'reviews', reviewerId),
  );

  useEffect(() => {
    batchDetailsRef.current = batchDetails;
  }, [batchDetails]);

  const refresh = useCallback(async () => {
    const storageScope = parseReviewStorageScope(searchQuery);
    const result = await catalog.identityReviewGroups(
      false,
      storageScope === undefined ? searchQuery : undefined,
      true,
    );
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    let nextGroups = result.data.groups;
    if (storageScope !== undefined) {
      try {
        const batchIds = await reviewStorageBatchIds(storageScope, async (type, entityId, page) => {
          const contents = await storage.contents(type, entityId, page);
          if (!contents.ok) throw new Error(contents.message ?? contents.error);
          return contents.data;
        });
        nextGroups = nextGroups.filter(
          (group) => group.batchId !== undefined && batchIds.has(group.batchId),
        );
      } catch (scopeError) {
        setError(scopeError instanceof Error ? scopeError.message : String(scopeError));
        return;
      }
    }
    setError(null);
    setGroups(nextGroups);
    setLoadedCards(new Map());
    setExpandedGroups(
      readStorageHierarchyExpansion(
        browserStorageHierarchyStateStore(),
        'reviews-batches',
        reviewerId,
      ),
    );
    setPreviewItems(new Map());
    setLoadingPreviews(new Set());
    setPreviewErrors(new Set());
    setBatchDetails(new Map());
  }, [catalog, reviewerId, searchQuery, storage]);

  const downloadManualOverrideReport = async () => {
    if (manualOverrideReportBusy) return;
    setManualOverrideReportBusy(true);
    setError(null);
    const reportCards: CardRecord[] = [];
    for (const status of ['DRAFT', 'VERIFIED'] as const) {
      let lastKey: string | undefined;
      do {
        const result = await catalog.list({
          manualIdentityOverrideStatus: status,
          limit: 100,
          ...(lastKey === undefined ? {} : { lastKey }),
        });
        if (!result.ok) {
          setManualOverrideReportBusy(false);
          setError(result.message ?? result.error);
          return;
        }
        reportCards.push(...result.data.items);
        lastKey = result.data.lastKey ?? undefined;
      } while (lastKey !== undefined);
    }
    const href = URL.createObjectURL(
      new Blob([manualIdentityOverrideCsv(reportCards)], { type: 'text/csv;charset=utf-8' }),
    );
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = `tcvs-active-manual-overrides-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(href);
    setManualOverrideReportBusy(false);
  };

  useEffect(() => {
    if (restorePending.current && initialSnapshot !== undefined) {
      restorePending.current = false;
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
      window.requestAnimationFrame(() => window.scrollTo({ top: initialSnapshot.scrollY }));
      return;
    }
    void refresh();
  }, [initialSnapshot, location.pathname, location.search, navigate, refresh]);

  const returnContext = (activeBatchId: string): ReviewListReturnContext | undefined => {
    if (groups === null) return undefined;
    return reviewListReturnContext({
      groups,
      loadedCards: [...loadedCards.entries()],
      expandedGroupKeys: [...expandedGroups],
      previewItems: [...previewItems.entries()],
      // Re-check only the batch the operator visited so progress is current on return.
      batchDetails: [...batchDetails.entries()].filter(([batchId]) => batchId !== activeBatchId),
      resultLimit,
      visibleCount,
      hideIdentityReviewed,
      hideImageReviewed,
      showInProgress,
      showCompleted,
      searchDraft,
      searchQuery,
      sortOrder,
      scrollY: Math.max(0, window.scrollY),
    });
  };

  const loadGroupPreview = useCallback(
    async (group: IdentityReviewGroupSummary) => {
      if (
        group.previewCards.length === 0 ||
        previewItems.has(group.key) ||
        loadingPreviews.has(group.key)
      ) {
        return;
      }
      setLoadingPreviews((previous) => new Set(previous).add(group.key));
      setPreviewErrors((previous) => {
        const next = new Set(previous);
        next.delete(group.key);
        return next;
      });
      const result = await catalog.identityReviewPreviews(
        group.previewCards.map((card) => card.cardId),
      );
      setLoadingPreviews((previous) => {
        const next = new Set(previous);
        next.delete(group.key);
        return next;
      });
      if (!result.ok) {
        setPreviewErrors((previous) => new Set(previous).add(group.key));
        return;
      }
      setPreviewItems((previous) => new Map(previous).set(group.key, result.data.items));
    },
    [catalog, loadingPreviews, previewItems],
  );

  const loadGroupCards = useCallback(
    async (group: IdentityReviewGroupSummary) => {
      if (loadedCards.has(group.key) || loadingGroups.has(group.key)) return;
      setLoadingGroups((previous) => new Set(previous).add(group.key));
      const result = await catalog.list({
        ...(group.batchId !== undefined
          ? { placementBatchId: group.batchId }
          : group.runId !== null
            ? { runId: group.runId }
            : {
                identityStatus: 'UNIDENTIFIED',
                reviewDismissed: false,
              }),
        limit: group.batchId !== undefined || group.runId !== null ? 500 : 100,
      });
      setLoadingGroups((previous) => {
        const next = new Set(previous);
        next.delete(group.key);
        return next;
      });
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      const cards = result.data.items.filter(
        (card) =>
          card.reviewDismissed !== true &&
          (group.batchId !== undefined
            ? placementBatchIdOf(card) === group.batchId
            : group.runId !== null
              ? placementBatchIdOf(card) === undefined && card.runId === group.runId
              : placementBatchIdOf(card) === undefined &&
                (card.runId === undefined || card.runId === '')),
      );
      setLoadedCards((previous) => new Map(previous).set(group.key, cards));
    },
    [catalog, loadedCards, loadingGroups],
  );

  const statusFilters = useMemo<IdentityReviewStatusFilters>(
    () => ({ hideIdentityReviewed, hideImageReviewed, showInProgress, showCompleted }),
    [hideIdentityReviewed, hideImageReviewed, showInProgress, showCompleted],
  );
  const sortedGroups = useMemo(
    () => (groups === null ? [] : sortIdentityReviewGroups(groups, sortOrder)),
    [groups, sortOrder],
  );
  const statusFilteredGroups = useMemo(
    () =>
      sortedGroups.filter((group) =>
        group.batchId === undefined
          ? true
          : identityReviewStatusVisible(
              batchDetails.get(group.batchId)?.batchStatus,
              statusFilters,
            ),
      ),
    [batchDetails, sortedGroups, statusFilters],
  );
  const visibleGroups = useMemo(
    () => visibleIdentityReviewGroups(statusFilteredGroups, visibleCount),
    [statusFilteredGroups, visibleCount],
  );
  useEffect(() => {
    visibleGroups
      .filter((group) => expandedGroups.has(group.key))
      .forEach((group) => void loadGroupCards(group));
  }, [expandedGroups, loadGroupCards, visibleGroups]);
  const visibleLocationGroups = useMemo(() => {
    if (storageGroupMode === 'none' || storageGroupMode === 'batch') {
      return [
        {
          level: 'batch' as const,
          key: 'all',
          label: 'Batches',
          nodeKey: 'reviews:all-batches',
          count: visibleGroups.length,
          items: visibleGroups,
        },
      ];
    }
    return buildStorageGroups(
      visibleGroups,
      (group) => identityReviewStorageHierarchyPath(group, batchDetails),
      storageGroupMode,
    );
  }, [batchDetails, storageGroupMode, visibleGroups]);
  const toggleLocationGroup = (nodeKey: string) => {
    setExpandedLocationGroups((previous) => {
      const next = new Set(previous);
      if (next.has(nodeKey)) next.delete(nodeKey);
      else next.add(nodeKey);
      writeStorageHierarchyExpansion(
        browserStorageHierarchyStateStore(),
        'reviews',
        reviewerId,
        next,
      );
      return next;
    });
  };
  const statusCandidateGroups = useMemo(() => {
    if (groups === null) return [];
    return hideIdentityReviewed || hideImageReviewed || showInProgress || showCompleted
      ? sortedGroups
      : visibleIdentityReviewGroups(sortedGroups, visibleCount);
  }, [
    groups,
    hideIdentityReviewed,
    hideImageReviewed,
    showInProgress,
    showCompleted,
    sortedGroups,
    visibleCount,
  ]);
  const statusBatchIds = useMemo(
    () =>
      statusCandidateGroups
        .map((group) => group.batchId)
        .filter((batchId): batchId is string => batchId !== undefined),
    [statusCandidateGroups],
  );

  useEffect(() => {
    const missing = statusBatchIds.filter((batchId) => !batchDetailsRef.current.has(batchId));
    if (missing.length === 0) return;
    let live = true;
    void loadIdentityReviewBatchDetails(
      missing,
      async (batchId) => {
        const result = await storage.entityDetail('batches', batchId);
        return result.ok ? result.data : null;
      },
      (details) => {
        if (!live) return false;
        setBatchDetails((previous) => {
          const next = new Map(previous);
          for (const [batchId, detail] of details) next.set(batchId, detail);
          batchDetailsRef.current = next;
          return next;
        });
        return true;
      },
    );
    return () => {
      live = false;
    };
  }, [statusBatchIds, storage]);

  const renderRow = (card: CardRecord): JSX.Element => {
    const suggested =
      card.suggestion?.identification.status === 'known'
        ? formatIdentity(card.suggestion.identification.identity)
        : 'no suggestion';
    return (
      <li key={card.cardId} style={{ padding: '6px 0', borderTop: '1px solid var(--tcvs-border)' }}>
        <ReviewCardPreviewLink cardId={card.cardId} shortCode={card.shortCode} />{' '}
        {card.identityReviewDraft === undefined ? null : (
          <span className="tcvs-status-badge tcvs-status-in-progress">In progress</span>
        )}{' '}
        <span className="tcvs-muted">· suggested: {suggested}</span>{' '}
        <span className="tcvs-muted">
          · images: {imageQualityStatusLabel(card.imageQualityStatus)}
        </span>{' '}
        <span className="tcvs-muted">· {shortDateTime(card.createdAt)}</span>{' '}
      </li>
    );
  };

  const heading = 'Identity review';
  const activeStorageScope = parseReviewStorageScope(searchQuery);
  const emptyText =
    activeStorageScope !== undefined
      ? 'No review batches were found in this storage container.'
      : searchQuery !== ''
        ? 'No review batches match the current search.'
        : 'No cards awaiting identity review.';
  const waitingForStatusFilters =
    (hideIdentityReviewed || hideImageReviewed || showInProgress || showCompleted) &&
    statusBatchIds.some((batchId) => !batchDetails.has(batchId));
  if (error !== null) return <p className="tcvs-error">Error: {error}</p>;

  return (
    <section style={{ marginBottom: 28 }}>
      <h3>{heading}</h3>
      <p className="tcvs-caption">
        Review each card&apos;s images, identity, and listing price in one submission. Flag an image
        issue only when the card needs later remediation.
      </p>
      <div
        className="tcvs-review-list-controls"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <form
          className="tcvs-review-search"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            const next = searchDraft.trim();
            setGroups(null);
            setVisibleCount(resultLimit);
            setBatchDetails(new Map());
            if (next === searchQuery) void refresh();
            else setSearchQuery(next);
          }}
        >
          <label>
            <span>Find review batches</span>
            <input
              type="search"
              value={searchDraft}
              placeholder="Card, set, SKU, OCR, or storage ID"
              onChange={(event) => setSearchDraft(event.target.value)}
            />
          </label>
          <button type="submit" className="tcvs-btn-compact">
            Search
          </button>
          {searchQuery !== '' ? (
            <button
              type="button"
              className="tcvs-link-button"
              onClick={() => {
                setSearchDraft('');
                setGroups(null);
                setSearchQuery('');
              }}
            >
              Clear
            </button>
          ) : null}
        </form>
        <fieldset className="tcvs-review-status-filters">
          <legend>Hide completed</legend>
          <label>
            <input
              type="checkbox"
              checked={hideIdentityReviewed}
              onChange={(event) => {
                setHideIdentityReviewed(event.currentTarget.checked);
                setVisibleCount(resultLimit);
              }}
            />
            <span>Identified batches</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={hideImageReviewed}
              onChange={(event) => {
                setHideImageReviewed(event.currentTarget.checked);
                setVisibleCount(resultLimit);
              }}
            />
            <span>Image-reviewed batches</span>
          </label>
        </fieldset>
        <fieldset className="tcvs-review-status-filters">
          <legend>Review state</legend>
          <label>
            <input
              type="checkbox"
              checked={showInProgress}
              onChange={(event) => {
                setShowInProgress(event.currentTarget.checked);
                setVisibleCount(resultLimit);
              }}
            />
            <span>In progress</span>
          </label>
          <label>
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(event) => {
                setShowCompleted(event.currentTarget.checked);
                setVisibleCount(resultLimit);
              }}
            />
            <span>Completed</span>
          </label>
        </fieldset>
        <StorageGroupingControl
          value={storageGroupMode}
          onChange={(mode) => {
            setStorageGroupMode(mode);
            writeStorageGroupMode(browserStorageHierarchyStateStore(), 'reviews', reviewerId, mode);
          }}
        />
        <label
          className="tcvs-field"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            margin: 0,
          }}
        >
          <span>Sort</span>
          <select
            aria-label="Identity review sort order"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value as IdentityReviewSortOrder)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
        <label
          className="tcvs-field"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            margin: 0,
          }}
        >
          <span>Results</span>
          <select
            aria-label="Identity review results"
            value={resultLimit}
            onChange={(event) => {
              const next = Number(event.target.value) as IdentityReviewResultLimit;
              setResultLimit(next);
              setVisibleCount(next);
              writeIdentityReviewResultLimitPreference(
                browserResultLimitStorage(),
                reviewerId,
                next,
              );
            }}
          >
            {IDENTITY_REVIEW_RESULT_LIMITS.map((limit) => (
              <option key={limit} value={limit}>
                {limit}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="tcvs-btn-secondary"
          disabled={manualOverrideReportBusy}
          onClick={() => void downloadManualOverrideReport()}
        >
          {manualOverrideReportBusy ? 'Preparing report…' : 'Download manual overrides'}
        </button>
      </div>
      {groups === null ? (
        <p>Loading…</p>
      ) : groups.length === 0 ? (
        <p>{emptyText}</p>
      ) : statusFilteredGroups.length === 0 && !waitingForStatusFilters ? (
        <p>No review batches match the current status filters.</p>
      ) : (
        <div>
          {waitingForStatusFilters ? (
            <p className="tcvs-caption">Checking selected status filters…</p>
          ) : null}
          <CollapsibleStorageGroups
            groups={visibleLocationGroups}
            expanded={expandedLocationGroups}
            onToggle={toggleLocationGroup}
            showHeaders={storageGroupMode !== 'batch' && storageGroupMode !== 'none'}
            countLabel={(group) => `${group.count} ${group.count === 1 ? 'batch' : 'batches'}`}
            renderItems={(locationGroups) => (
              <>
                {locationGroups.map((group) => {
                  const expanded = expandedGroups.has(group.key);
                  const cards = loadedCards.get(group.key);
                  const loading = loadingGroups.has(group.key);
                  const preview = previewItems.get(group.key);
                  const previewLoading = loadingPreviews.has(group.key);
                  const previewFailed = previewErrors.has(group.key);
                  const batchStatus =
                    group.batchId === undefined
                      ? undefined
                      : batchDetails.get(group.batchId)?.batchStatus;
                  const reviewStartedCount =
                    batchStatus === undefined || batchStatus === null
                      ? undefined
                      : (batchStatus.review?.startedCount ?? 0);
                  return (
                    <section key={group.key} className="tcvs-review-batch-group">
                      <div className="tcvs-review-batch-heading">
                        <button
                          type="button"
                          className="tcvs-review-batch-toggle"
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Collapse' : 'Expand'} ${identityReviewHeading(group)}`}
                          title={`${expanded ? 'Collapse' : 'Expand'} ${identityReviewHeading(group)}`}
                          onClick={() => {
                            const opening = !expanded;
                            setExpandedGroups((previous) => {
                              const next = new Set(previous);
                              if (next.has(group.key)) next.delete(group.key);
                              else next.add(group.key);
                              writeStorageHierarchyExpansion(
                                browserStorageHierarchyStateStore(),
                                'reviews-batches',
                                reviewerId,
                                next,
                              );
                              return next;
                            });
                            if (opening) void loadGroupCards(group);
                          }}
                        >
                          <span className="tcvs-batch-disclosure-icon" aria-hidden="true">
                            {expanded ? '-' : '+'}
                          </span>
                          {group.batchId === undefined ? (
                            <span className="tcvs-mono" title={group.runId ?? undefined}>
                              {identityReviewHeading(group)}
                            </span>
                          ) : null}
                        </button>
                        {group.batchId === undefined ? null : (
                          <Link
                            className="tcvs-review-batch-link tcvs-review-gallery-trigger tcvs-mono"
                            title={group.batchId}
                            to={`/batches/${encodeURIComponent(group.batchId)}/identify`}
                            onClick={(event) => {
                              if (
                                event.button !== 0 ||
                                event.metaKey ||
                                event.ctrlKey ||
                                event.shiftKey ||
                                event.altKey
                              ) {
                                return;
                              }
                              event.preventDefault();
                              const context = returnContext(group.batchId!);
                              navigate(`${location.pathname}${location.search}`, {
                                replace: true,
                                state: context,
                              });
                              navigate(`/batches/${encodeURIComponent(group.batchId!)}/identify`, {
                                state: context,
                              });
                            }}
                            onPointerEnter={() => void loadGroupPreview(group)}
                            onFocus={() => void loadGroupPreview(group)}
                          >
                            {identityReviewHeading(group)}
                            <span className="tcvs-review-hover-gallery" aria-hidden="true">
                              {previewLoading ? (
                                <span className="tcvs-review-gallery-empty">Loading preview…</span>
                              ) : previewFailed ? (
                                <span className="tcvs-review-gallery-empty">
                                  Preview unavailable
                                </span>
                              ) : preview === undefined ? (
                                <span className="tcvs-review-gallery-empty">
                                  Hover to load preview
                                </span>
                              ) : preview.length === 0 ? (
                                <span className="tcvs-review-gallery-empty">No preview images</span>
                              ) : (
                                preview.map((card) =>
                                  card.imageUrl === undefined ? (
                                    <span key={card.cardId} className="tcvs-review-gallery-empty">
                                      {card.shortCode ?? card.cardId.slice(0, 6)}
                                    </span>
                                  ) : (
                                    <img
                                      key={card.cardId}
                                      src={card.imageUrl}
                                      alt=""
                                      title={card.shortCode ?? card.cardId}
                                    />
                                  ),
                                )
                              )}
                            </span>
                          </Link>
                        )}
                        <span className="tcvs-muted">
                          (
                          {searchQuery === '' || activeStorageScope !== undefined
                            ? group.count
                            : `${group.count} matching ${group.count === 1 ? 'card' : 'cards'}`}
                          )
                        </span>
                        {group.batchId === undefined ? null : (
                          <span className="tcvs-storage-location-breadcrumb">
                            {storageLocationBreadcrumb(
                              identityReviewStorageHierarchyPath(group, batchDetails),
                              storageGroupMode,
                            )}
                          </span>
                        )}
                        {group.batchId === undefined ? null : batchStatus === undefined ? (
                          <span className="tcvs-muted">Checking progress…</span>
                        ) : batchStatus === null ? (
                          <span className="tcvs-muted">Progress unavailable</span>
                        ) : identityReviewProgressState(batchStatus) === 'COMPLETED' ? (
                          <span className="tcvs-status-badge tcvs-status-complete">
                            Completed ·{' '}
                            {batchStatus.review?.completedCount ?? batchStatus.cardCount}/
                            {batchStatus.cardCount}
                          </span>
                        ) : identityReviewProgressState(batchStatus) === 'IN_PROGRESS' ? (
                          <span className="tcvs-status-badge tcvs-status-in-progress">
                            In progress · {reviewStartedCount}/
                            {batchStatus?.cardCount ?? group.count} started
                          </span>
                        ) : (
                          <span className="tcvs-status-badge tcvs-status-not-started">
                            Not started
                          </span>
                        )}
                        {batchStatus?.preliminaryImageReview === undefined ? null : (
                          <span
                            className={`tcvs-status-badge ${
                              batchStatus.preliminaryImageReviewComplete === true
                                ? 'tcvs-status-complete'
                                : 'tcvs-status-not-started'
                            }`}
                          >
                            Image review {batchStatus.preliminaryImageReview.reviewedCount}/
                            {batchStatus.cardCount}
                          </span>
                        )}
                      </div>
                      {expanded ? (
                        loading || cards === undefined ? (
                          <p className="tcvs-muted">Loading batch cards…</p>
                        ) : (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {cards.map(renderRow)}
                          </ul>
                        )
                      ) : null}
                    </section>
                  );
                })}
              </>
            )}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 12,
              flexWrap: 'wrap',
            }}
          >
            <span className="tcvs-caption">
              Showing {Math.min(visibleCount, statusFilteredGroups.length)} of{' '}
              {statusFilteredGroups.length}
            </span>
            {visibleCount < statusFilteredGroups.length ? (
              <button
                type="button"
                className="tcvs-btn-compact"
                onClick={() =>
                  setVisibleCount((current) => nextIdentityReviewVisibleCount(current, resultLimit))
                }
              >
                Load more
              </button>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}

export function ImageQaReviewSection(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const catalog = useCatalogApi();
  const [items, setItems] = useState<CardRecord[] | null>(null);
  const [view, setView] = useState<ImageApprovalView>('PENDING');
  const [selectedRunId, setSelectedRunId] = useState<string | null | undefined>(() => {
    const run = params.get('run');
    return run === null ? undefined : run === '__none__' ? null : run;
  });
  const [index, setIndex] = useState(0);
  const [detail, setDetail] = useState<CatalogDetailResponse | null>(null);
  const [busyAction, setBusyAction] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [popoutOpen, setPopoutOpen] = useState(false);
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);

  const refresh = useCallback(async () => {
    if (view === 'ALL') {
      const results = await Promise.all(
        IMAGE_APPROVAL_STATUSES.map((status) =>
          catalog.list({ imageQualityStatus: status, limit: 100 }),
        ),
      );
      const byCardId = new Map<string, CardRecord>();
      for (const result of results) {
        if (!result.ok) {
          setError(result.message ?? result.error);
          return;
        }
        for (const card of result.data.items) {
          byCardId.set(card.cardId, card);
        }
      }
      setError(null);
      setItems([...byCardId.values()]);
      setDetail(null);
      return;
    }

    const result = await catalog.list({ imageQualityStatus: view, limit: 100 });
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    setError(null);
    setItems(result.data.items);
    setDetail(null);
  }, [catalog, view]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups = items === null ? [] : groupCardsByRun(items);
  const selectedGroup = groups.find((group) => group.runId === selectedRunId) ?? groups[0] ?? null;
  const current = selectedGroup?.cards[index] ?? selectedGroup?.cards[0] ?? null;
  const imageReview = useVersionedImageReview(
    detail?.cardId === current?.cardId ? detail : null,
    catalog,
    'FINAL',
  );
  const pendingReview = imageReview.required && detail?.pendingImageReplacement !== undefined;
  const busy =
    busyAction !== null ||
    detail?.cardId !== current?.cardId ||
    !imageReview.ready ||
    pendingReview;
  const displayedDetail = pendingReview ? null : imageReview.card;
  const reviewStatus = (
    <>
      <VersionedImageReviewStatus {...imageReview} />
      {pendingReview && current !== null ? (
        <p>
          <Link to={`/catalog/${current.cardId}`}>Review replacement images in catalog detail</Link>
        </p>
      ) : null}
    </>
  );
  const canGoPrevious = index > 0;
  const canGoNext = selectedGroup !== null && index < selectedGroup.cards.length - 1;

  useEffect(() => {
    if (selectedRunId !== selectedGroup?.runId) {
      setSelectedRunId(selectedGroup?.runId);
      setIndex(0);
    }
  }, [selectedGroup?.runId, selectedRunId]);

  useEffect(() => {
    if (current === null) {
      setDetail(null);
      return;
    }
    let canceled = false;
    setDetail(null);
    const load = async () => {
      const result = await catalog.get(current.cardId);
      if (canceled) return;
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      setError(null);
      setDetail(result.data);
    };
    void load();
    return () => {
      canceled = true;
    };
  }, [catalog, current]);

  useEffect(() => {
    if (current === null) {
      setPopoutOpen(false);
      if (popoutWindow !== null && !popoutWindow.closed) {
        popoutWindow.close();
      }
      setPopoutWindow(null);
    }
  }, [current, popoutWindow]);

  const advanceAfterDecision = async () => {
    const nextLength = (selectedGroup?.cards.length ?? 1) - 1;
    await refresh();
    setIndex((prev) => Math.min(prev, Math.max(0, nextLength - 1)));
  };

  const approve = async () => {
    if (current === null || current.imageQualityStatus === 'ACCEPTED' || busy) return;
    setBusyAction('approve');
    const result =
      imageReview.reviewId === undefined
        ? await catalog.updateImageQuality(current.cardId, 'ACCEPTED')
        : await catalog.updateImageQuality(
            current.cardId,
            'ACCEPTED',
            undefined,
            imageReview.reviewId,
          );
    imageReview.reload();
    setBusyAction(null);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    await advanceAfterDecision();
  };

  const goPrevious = () => setIndex((prev) => Math.max(0, prev - 1));

  const goNext = () => {
    setIndex((prev) =>
      selectedGroup === null ? prev : Math.min(selectedGroup.cards.length - 1, prev + 1),
    );
  };

  const closePopout = useCallback(() => {
    setPopoutOpen(false);
    if (popoutWindow !== null && !popoutWindow.closed) {
      popoutWindow.close();
    }
    setPopoutWindow(null);
  }, [popoutWindow]);

  const openPopout = useCallback(() => {
    const popup = window.open('', 'tcvs-image-approval-popout', 'popup=yes,width=1440,height=980');
    if (popup === null) {
      setPopoutWindow(null);
      setPopoutOpen(true);
      return;
    }
    prepareImageQaPopoutWindow(popup);
    setPopoutWindow(popup);
    setPopoutOpen(true);
  }, []);

  useEffect(() => {
    if (!popoutOpen || popoutWindow !== null) return;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePopout();
      }
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closePopout, popoutOpen, popoutWindow]);

  const reject = async (confirmWindow: Window | null = window) => {
    if (current === null || busy) return;
    const targetWindow = confirmWindow ?? window;
    if (
      !targetWindow.confirm(
        'Reject this card for Image Approval? It will stay in the system but will not be eligible for marketplace listing.',
      )
    ) {
      return;
    }
    setBusyAction('reject');
    const result = await catalog.updateImageQuality(
      current.cardId,
      'REJECTED',
      'Rejected during Image Approval',
    );
    imageReview.reload();
    setBusyAction(null);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    await advanceAfterDecision();
  };

  return (
    <section>
      <h3>Image Approval</h3>
      <p className="tcvs-caption">
        Review captured front/back images before marketplace listing. Approve good images; reject
        bad images so the card can be re-imaged in a later run.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {IMAGE_APPROVAL_VIEWS.map((nextView) => (
          <button
            key={nextView}
            type="button"
            className="tcvs-btn-compact"
            aria-pressed={view === nextView}
            disabled={view === nextView}
            onClick={() => {
              setView(nextView);
              setItems(null);
              setSelectedRunId(undefined);
              setParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('section', 'Image Approval');
                  next.delete('run');
                  return next;
                },
                { replace: true },
              );
              setIndex(0);
            }}
          >
            {imageApprovalViewLabel(nextView)}
          </button>
        ))}
      </div>
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
      {items === null ? (
        <p>Loading…</p>
      ) : groups.length === 0 ? (
        <p>{emptyImageApprovalText(view)}</p>
      ) : (
        <div className="tcvs-image-approval-review-layout">
          <div className="tcvs-image-approval-batch-list">
            <h4>Batches</h4>
            <div className="tcvs-image-approval-batch-buttons">
              {groups.map((group) => (
                <button
                  key={group.runId ?? '__none__'}
                  type="button"
                  className={
                    group.runId === selectedGroup?.runId
                      ? 'tcvs-btn-compact tcvs-nav-active'
                      : 'tcvs-btn-compact'
                  }
                  aria-pressed={group.runId === selectedGroup?.runId}
                  title={group.runId ?? undefined}
                  onClick={() => {
                    setSelectedRunId(group.runId);
                    setParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('section', 'Image Approval');
                        if (group.runId === null) next.set('run', '__none__');
                        else next.set('run', group.runId);
                        return next;
                      },
                      { replace: true },
                    );
                    setIndex(0);
                  }}
                >
                  {runHeading(group.runId)} ({group.cards.length})
                </button>
              ))}
            </div>
          </div>
          <div>
            {current === null ? (
              <p>No card selected.</p>
            ) : (
              <>
                <ImageQaReviewPanel
                  current={current}
                  detail={displayedDetail}
                  reviewStatus={reviewStatus}
                  currentIndex={index}
                  totalCards={selectedGroup?.cards.length ?? 1}
                  busy={busy}
                  busyAction={busyAction}
                  canGoPrevious={canGoPrevious}
                  canGoNext={canGoNext}
                  onPrevious={goPrevious}
                  onNext={goNext}
                  onApprove={() => void approve()}
                  onReject={(confirmWindow) => void reject(confirmWindow)}
                  onPopout={openPopout}
                />
              </>
            )}
          </div>
        </div>
      )}
      {popoutOpen && current !== null && popoutWindow === null ? (
        <ImageQaPopout
          current={current}
          detail={displayedDetail}
          reviewStatus={reviewStatus}
          currentIndex={index}
          totalCards={selectedGroup?.cards.length ?? 1}
          busy={busy}
          busyAction={busyAction}
          onApprove={() => void approve()}
          onReject={(confirmWindow) => void reject(confirmWindow)}
          onClose={closePopout}
        />
      ) : null}
      {popoutOpen && current !== null && popoutWindow !== null ? (
        <ImageQaPopoutWindow
          popup={popoutWindow}
          current={current}
          detail={displayedDetail}
          reviewStatus={reviewStatus}
          currentIndex={index}
          totalCards={selectedGroup?.cards.length ?? 1}
          busy={busy}
          busyAction={busyAction}
          onApprove={() => void approve()}
          onReject={(confirmWindow) => void reject(confirmWindow)}
          onClose={closePopout}
        />
      ) : null}
    </section>
  );
}

function prepareImageQaPopoutWindow(popup: Window): void {
  popup.document.open();
  popup.document.write(
    '<!doctype html><html><head><title>Image Approval</title></head><body></body></html>',
  );
  popup.document.close();
  popup.document.body.style.margin = '0';
  popup.document.body.style.background = 'var(--tcvs-bg, #050617)';
  popup.document.body.style.color = 'var(--tcvs-text, #f7f8ff)';

  for (const node of Array.from(document.head.children)) {
    if (
      node instanceof HTMLLinkElement &&
      (node.rel === 'stylesheet' || node.rel === 'preload' || node.rel === 'modulepreload')
    ) {
      const clone = node.cloneNode(true) as HTMLLinkElement;
      clone.href = node.href;
      popup.document.head.appendChild(clone);
    }
    if (node instanceof HTMLStyleElement) {
      popup.document.head.appendChild(node.cloneNode(true));
    }
  }
}

function ImageQaPopoutWindow({
  popup,
  current,
  detail,
  reviewStatus,
  currentIndex,
  totalCards,
  busy,
  busyAction,
  onApprove,
  onReject,
  onClose,
}: {
  popup: Window;
  current: CardRecord;
  detail: CatalogDetailResponse | null;
  reviewStatus: ReactNode;
  currentIndex: number;
  totalCards: number;
  busy: boolean;
  busyAction: 'approve' | 'reject' | null;
  onApprove: () => void;
  onReject: (confirmWindow: Window | null) => void;
  onClose: () => void;
}): JSX.Element | null {
  useEffect(() => {
    const poll = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(poll);
        onClose();
      }
    }, 250);
    return () => window.clearInterval(poll);
  }, [popup, onClose]);

  if (popup.closed) {
    return null;
  }

  return createPortal(
    <ImageQaPopout
      current={current}
      detail={detail}
      reviewStatus={reviewStatus}
      currentIndex={currentIndex}
      totalCards={totalCards}
      busy={busy}
      busyAction={busyAction}
      onApprove={onApprove}
      onReject={onReject}
      onClose={onClose}
    />,
    popup.document.body,
  );
}

function ImageQaReviewPanel({
  current,
  detail,
  reviewStatus,
  currentIndex,
  totalCards,
  busy,
  busyAction,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
  onApprove,
  onReject,
  onPopout,
}: {
  current: CardRecord;
  detail: CatalogDetailResponse | null;
  reviewStatus: ReactNode;
  currentIndex: number;
  totalCards: number;
  busy: boolean;
  busyAction: 'approve' | 'reject' | null;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onApprove: () => void;
  onReject: (confirmWindow: Window | null) => void;
  onPopout: () => void;
}): JSX.Element {
  return (
    <>
      <ImageQaReviewToolbar
        current={current}
        currentIndex={currentIndex}
        totalCards={totalCards}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        onPrevious={onPrevious}
        onNext={onNext}
        onPopout={onPopout}
      />
      {reviewStatus}
      <ImageQaImageGrid detail={detail} fullscreen={false} />
      <ImageQaDecisionActions
        current={current}
        busy={busy}
        busyAction={busyAction}
        onApprove={onApprove}
        onReject={onReject}
      >
        <Link to={`/catalog/${current.cardId}`}>Open catalog detail</Link>
      </ImageQaDecisionActions>
    </>
  );
}

function ImageQaPopout({
  current,
  detail,
  reviewStatus,
  currentIndex,
  totalCards,
  busy,
  busyAction,
  onApprove,
  onReject,
  onClose,
}: {
  current: CardRecord;
  detail: CatalogDetailResponse | null;
  reviewStatus: ReactNode;
  currentIndex: number;
  totalCards: number;
  busy: boolean;
  busyAction: 'approve' | 'reject' | null;
  onApprove: () => void;
  onReject: (confirmWindow: Window | null) => void;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="tcvs-image-approval-popout" role="dialog" aria-modal="true">
      <header className="tcvs-image-approval-popout-header">
        <div>
          <h3>Image Approval</h3>
          <ImageQaCardMeta current={current} currentIndex={currentIndex} totalCards={totalCards} />
        </div>
        <div className="tcvs-image-approval-popout-nav">
          <button
            type="button"
            className="tcvs-icon-button tcvs-image-approval-popout-close"
            onClick={onClose}
            title="Close"
            aria-label="Close Image Approval pop-out"
          >
            <span aria-hidden="true">X</span>
          </button>
        </div>
      </header>
      <main className="tcvs-image-approval-popout-main">
        {reviewStatus}
        <ImageQaImageGrid detail={detail} fullscreen />
      </main>
      <footer className="tcvs-image-approval-popout-footer">
        <ImageQaDecisionActions
          current={current}
          busy={busy}
          busyAction={busyAction}
          onApprove={onApprove}
          onReject={onReject}
        />
      </footer>
    </div>
  );
}

function ImageQaReviewToolbar({
  current,
  currentIndex,
  totalCards,
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
  onPopout,
}: {
  current: CardRecord;
  currentIndex: number;
  totalCards: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onPopout: () => void;
}): JSX.Element {
  return (
    <div className="tcvs-image-approval-toolbar">
      <ImageQaCardMeta current={current} currentIndex={currentIndex} totalCards={totalCards} />
      <div className="tcvs-image-approval-toolbar-actions">
        <ImageQaNavigationControls
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          onPrevious={onPrevious}
          onNext={onNext}
        />
        <button
          type="button"
          className="tcvs-icon-button"
          onClick={onPopout}
          title="Pop out"
          aria-label="Pop out Image Approval"
        >
          <span className="tcvs-popout-icon" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function ImageQaNavigationControls({
  canGoPrevious,
  canGoNext,
  onPrevious,
  onNext,
}: {
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
}): JSX.Element {
  return (
    <>
      <button
        type="button"
        className="tcvs-btn-compact"
        disabled={!canGoPrevious}
        onClick={onPrevious}
      >
        Previous
      </button>
      <button type="button" className="tcvs-btn-compact" disabled={!canGoNext} onClick={onNext}>
        Next
      </button>
    </>
  );
}

function ImageQaCardMeta({
  current,
  currentIndex,
  totalCards,
}: {
  current: CardRecord;
  currentIndex: number;
  totalCards: number;
}): JSX.Element {
  return (
    <div>
      <strong className="tcvs-mono">{current.shortCode ?? current.cardId}</strong>{' '}
      <span className="tcvs-muted">
        {currentIndex + 1} of {totalCards}
      </span>
      <span className="tcvs-muted"> · {imageQualityStatusLabel(current.imageQualityStatus)}</span>
    </div>
  );
}

function ImageQaImageGrid({
  detail,
  fullscreen,
}: {
  detail: CatalogDetailResponse | null;
  fullscreen: boolean;
}): JSX.Element {
  if (detail === null) {
    return <p>Loading images…</p>;
  }

  return (
    <div
      className={
        fullscreen ? 'tcvs-image-approval-grid tcvs-image-approval-grid-fullscreen' : undefined
      }
      style={
        fullscreen
          ? undefined
          : {
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
              marginBottom: 12,
            }
      }
    >
      <ImageQaSide
        side="Front"
        url={detail.imageUrls === undefined ? detail.imageUrl : detail.imageUrls.front}
        fullscreen={fullscreen}
      />
      <ImageQaSide side="Back" url={detail.imageUrls?.back} fullscreen={fullscreen} />
    </div>
  );
}

function ImageQaDecisionActions({
  current,
  busy,
  busyAction,
  onApprove,
  onReject,
  children,
}: {
  current: CardRecord;
  busy: boolean;
  busyAction: 'approve' | 'reject' | null;
  onApprove: () => void;
  onReject: (confirmWindow: Window | null) => void;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className="tcvs-image-approval-actions">
      <button
        type="button"
        className="tcvs-btn-compact"
        disabled={busy || current.imageQualityStatus === 'ACCEPTED'}
        onClick={onApprove}
      >
        {busyAction === 'approve' ? 'Approving...' : 'Approve'}
      </button>
      <button
        type="button"
        className="tcvs-row-menu-item-danger tcvs-btn-compact"
        disabled={busy}
        onClick={(event) => onReject(event.currentTarget.ownerDocument.defaultView)}
      >
        {busyAction === 'reject' ? 'Rejecting...' : 'Reject'}
      </button>
      {children}
    </div>
  );
}

function ImageQaSide({
  side,
  url,
  fullscreen = false,
}: {
  side: string;
  url?: string;
  fullscreen?: boolean;
}): JSX.Element {
  return (
    <figure
      className={fullscreen ? 'tcvs-image-approval-side-fullscreen' : undefined}
      style={{ margin: 0 }}
    >
      <figcaption style={{ marginBottom: 6 }}>{side}</figcaption>
      {url === undefined ? (
        <p className="tcvs-muted">(no image)</p>
      ) : fullscreen ? (
        <img
          className="tcvs-image-approval-image-fullscreen"
          src={url}
          alt={`${side} card image`}
        />
      ) : (
        <img className="tcvs-card-image" src={url} alt={`${side} card image`} />
      )}
    </figure>
  );
}
